import { ApplyParamsRecordEffectSpecID, UpdateRecordEffectSpecID } from "../../database/behaviour/effects/GadgetEffectLiterals";
import { UniqueValidationId } from "../../database/validations/keys";
import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCustomerKey,
  ShopifyCustomerPaymentMethodKey,
  ShopifyCustomerPaymentMethodToCustomerKey,
  ShopifyCustomerPaymentMethodToShopKey,
  ShopifyCustomerPaymentMethodUniquenessValidationKey,
  ShopifyShopKey,
  ShopifySubscriptionContractKey,
  ShopifySubscriptionContractToCustomerPaymentMethodKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCustomerPaymentMethodKey,
  defaultFilterableApiIdentifiers: ["adminGraphqlApiId"],
  "2022-01": {
    name: "Shopify Customer Payment Method",
    fields: [
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCustomerPaymentMethodToCustomerKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "customer_id",
            graphQLFieldKey: "customer",
            graphQLSelection: `id`,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Admin GraphQL API ID",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "admin_graphql_api_id",
            graphQLFieldKey: "id",
          },
        },
        validations: {
          [ShopifyCustomerPaymentMethodUniquenessValidationKey]: {
            key: ShopifyCustomerPaymentMethodUniquenessValidationKey,
            specID: UniqueValidationId,
          },
        },
      },
      {
        name: "Instrument",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "payment_instrument",
            graphQLFieldKey: "instrument",
            graphQLSelection: `...on CustomerCreditCard {
          billingAddress { address1 city country countryCode province provinceCode zip }
          brand expiresSoon expiryMonth expiryYear firstDigits isRevocable lastDigits maskedNumber name source virtualLastDigits
        }
        ...on CustomerPaypalBillingAgreement {
          billingAddress { address1 city country countryCode name province provinceCode zip }
          inactive isRevocable paypalAccountEmail
        }
        ...on CustomerShopPayAgreement {
          expiresSoon expiryMonth expiryYear inactive isRevocable lastDigits maskedNumber name
        }`,
          },
        },
      },
      {
        name: "Revoked At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "revoked_at",
            graphQLFieldKey: "revokedAt",
          },
        },
      },
      {
        name: "Revoked Reason",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "revoked_reason",
            graphQLFieldKey: "revokedReason",
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowMultiple: false,
          options: [
            { name: "AUTHORIZE_NET_GATEWAY_NOT_ENABLED" },
            { name: "AUTHORIZE_NET_RETURNED_NO_PAYMENT_METHOD" },
            { name: "FAILED_TO_UPDATE_CREDIT_CARD" },
            { name: "MANUALLY_REVOKED" },
            { name: "STRIPE_API_AUTHENTICATION_ERROR" },
            { name: "STRIPE_API_INVALID_REQUEST_ERROR" },
            { name: "STRIPE_GATEWAY_NOT_ENABLED" },
            { name: "STRIPE_PAYMENT_METHOD_NOT_CARD" },
            { name: "STRIPE_RETURNED_NO_PAYMENT_METHOD" },
          ],
        },
      },
      {
        name: "Subscription Contracts",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "subscriptionContracts" } },
        configuration: {
          relatedModelKey: ShopifySubscriptionContractKey,
          inverseFieldKey: ShopifySubscriptionContractToCustomerPaymentMethodKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCustomerPaymentMethodToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    stateChart: {
      includeCRUD: true,
      actions: [
        {
          name: "Revoke",
          from: "created",
          to: "created",
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
      ],
    },
    syncConfig: {
      type: "graphql_sublist",
      resource: "customer.paymentMethods",
      // With Customer REST sync
      parent: "customer",
      limit: 2,
      extraArgs: { showRevoked: true },
    },
  },
  "2023-10": {
    fields: [
      {
        name: "Revoked Reason",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "revoked_reason",
            graphQLFieldKey: "revokedReason",
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [
            { name: "AUTHORIZE_NET_GATEWAY_NOT_ENABLED" },
            { name: "AUTHORIZE_NET_RETURNED_NO_PAYMENT_METHOD" },
            { name: "FAILED_TO_UPDATE_CREDIT_CARD" },
            { name: "MANUALLY_REVOKED" },
            { name: "STRIPE_API_AUTHENTICATION_ERROR" },
            { name: "STRIPE_API_INVALID_REQUEST_ERROR" },
            { name: "STRIPE_GATEWAY_NOT_ENABLED" },
            { name: "STRIPE_PAYMENT_METHOD_NOT_CARD" },
            { name: "STRIPE_RETURNED_NO_PAYMENT_METHOD" },
            { name: "MERGED" },
            { name: "BRAINTREE_API_AUTHENTICATION_ERROR" },
            { name: "BRAINTREE_GATEWAY_NOT_ENABLED" },
            { name: "BRAINTREE_PAYMENT_METHOD_NOT_CARD" },
            { name: "BRAINTREE_RETURNED_NO_PAYMENT_METHOD" },
          ],
        },
      },
    ],
  },
  "2024-10": {
    fields: [
      {
        name: "Revoked Reason",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "revoked_reason",
            graphQLFieldKey: "revokedReason",
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [
            { name: "AUTHORIZE_NET_GATEWAY_NOT_ENABLED" },
            { name: "AUTHORIZE_NET_RETURNED_NO_PAYMENT_METHOD" },
            { name: "FAILED_TO_UPDATE_CREDIT_CARD" },
            { name: "MANUALLY_REVOKED" },
            { name: "STRIPE_API_AUTHENTICATION_ERROR" },
            { name: "STRIPE_API_INVALID_REQUEST_ERROR" },
            { name: "STRIPE_GATEWAY_NOT_ENABLED" },
            { name: "STRIPE_PAYMENT_METHOD_NOT_CARD" },
            { name: "STRIPE_RETURNED_NO_PAYMENT_METHOD" },
            { name: "MERGED" },
            { name: "BRAINTREE_API_AUTHENTICATION_ERROR" },
            { name: "BRAINTREE_GATEWAY_NOT_ENABLED" },
            { name: "BRAINTREE_PAYMENT_METHOD_NOT_CARD" },
            { name: "BRAINTREE_RETURNED_NO_PAYMENT_METHOD" },
            { name: "FAILED_TO_RETRIEVE_BILLING_ADDRESS" },
          ],
        },
      },
    ],
  },
  "2025-01": {
    fields: [
      {
        name: "Revoked Reason",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "revoked_reason",
            graphQLFieldKey: "revokedReason",
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [
            { name: "AUTHORIZE_NET_GATEWAY_NOT_ENABLED" },
            { name: "AUTHORIZE_NET_RETURNED_NO_PAYMENT_METHOD" },
            { name: "FAILED_TO_UPDATE_CREDIT_CARD" },
            { name: "MANUALLY_REVOKED" },
            { name: "STRIPE_API_AUTHENTICATION_ERROR" },
            { name: "STRIPE_API_INVALID_REQUEST_ERROR" },
            { name: "STRIPE_GATEWAY_NOT_ENABLED" },
            { name: "STRIPE_PAYMENT_METHOD_NOT_CARD" },
            { name: "STRIPE_RETURNED_NO_PAYMENT_METHOD" },
            { name: "MERGED" },
            { name: "BRAINTREE_API_AUTHENTICATION_ERROR" },
            { name: "BRAINTREE_GATEWAY_NOT_ENABLED" },
            { name: "BRAINTREE_PAYMENT_METHOD_NOT_CARD" },
            { name: "BRAINTREE_RETURNED_NO_PAYMENT_METHOD" },
            { name: "FAILED_TO_RETRIEVE_BILLING_ADDRESS" },
            { name: "PAYMENT_METHOD_VERIFICATION_FAILED" },
            { name: "THREE_D_SECURE_FLOW_IN_VERIFICATION_NOT_IMPLEMENTED" },
            { name: "CUSTOMER_REDACTED" },
            { name: "TOO_MANY_CONSECUTIVE_FAILURES" },
            { name: "CVV_ATTEMPTS_LIMIT_EXCEEDED" },
          ],
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCustomerPaymentMethodToCustomerKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "customer_id",
            graphQLFieldKey: "customer",
            graphQLSelection: `id`,
            availableInWebhook: ["customer_payment_methods/create", "customer_payment_methods/update", "customer_payment_methods/revoke"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Admin GraphQL API ID",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "admin_graphql_api_id",
            graphQLFieldKey: "id",
            availableInWebhook: ["customer_payment_methods/create", "customer_payment_methods/update", "customer_payment_methods/revoke"],
          },
        },
        validations: {
          [ShopifyCustomerPaymentMethodUniquenessValidationKey]: {
            key: ShopifyCustomerPaymentMethodUniquenessValidationKey,
            specID: UniqueValidationId,
          },
        },
      },
      {
        name: "Instrument",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "payment_instrument",
            availableInWebhook: ["customer_payment_methods/create", "customer_payment_methods/update", "customer_payment_methods/revoke"],
            graphQLFieldKey: "instrument",
            graphQLSelection: `...on CustomerCreditCard {
          billingAddress { address1 city country countryCode province provinceCode zip }
          brand expiresSoon expiryMonth expiryYear firstDigits isRevocable lastDigits maskedNumber name source virtualLastDigits
        }
        ...on CustomerPaypalBillingAgreement {
          billingAddress { address1 city country countryCode name province provinceCode zip }
          inactive isRevocable paypalAccountEmail
        }
        ...on CustomerShopPayAgreement {
          expiresSoon expiryMonth expiryYear inactive isRevocable lastDigits maskedNumber name
        }`,
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      resource: "customer.paymentMethods",
      // With Customer GraphQL sync
      parent: "customers",
      limit: 2,
      extraArgs: { showRevoked: true },
    },
  },
};

export const ShopifyCustomerPaymentMethod = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
